import React, { useCallback, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useDispatch, useSelector } from 'react-redux';
import { Map } from 'immutable';
import isEqual from 'lodash/isEqual';

import DsRoot from 'src/component/ds/DsRoot';
import DeviceSummaryBar from 'src/screen/v2/Protected/DashboardScreen/DeviceSummaryBar';
import FacilityMap from 'src/screen/v2/Protected/DashboardScreen/FacilityMap';
import { fetchFacilitySummary } from 'src/module/device/action';
import { getClientId } from 'src/module/authentication/selector';
import { getDevicesSelectedFiltersObj } from 'src/module/filter/selector';
import { getFacilitySummary } from 'src/module/device/selector';
import { isWhitelabel } from 'src/utils/utils';
import { pushHistory } from 'src/module/navigation/action';
import { setSelectedAttribute } from 'src/module/filter/action';
import { useTranslation } from 'src/i18n';


const DEVICE_LIST_PATH = '/devices/list';

export default function DashboardScreen () {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const title = isWhitelabel() ? 'ATCOM' : 'SmartTrace';


  const selectedFilters = useSelector(getDevicesSelectedFiltersObj, isEqual);
  const clientId = useSelector(getClientId);
  const summary = useSelector(getFacilitySummary);

  /* Only skeleton when there is nothing to show. A refetch (filter change,
   * refresh) keeps the previous counts up rather than flashing placeholders,
   * and `summary.isEmpty()` covers the first render, before `loading` is set. */
  const showSkeletons = summary.isEmpty();

  /*
   * The pills set the attribute filter on the way out to the device list, so
   * coming back would leave it applied to a screen with no chip to show or
   * clear it -- and every future v2 component here would inherit it. The area
   * and unit filters are deliberately left alone; those persist by design.
   */
  useEffect(() => {
    dispatch(setSelectedAttribute(null));
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchFacilitySummary(Map(selectedFilters)));
  }, [dispatch, clientId, selectedFilters]);

  /*
   * Drill in to the devices matching a pill. Both filters ride in redux: the
   * attribute is set here, and the area/unit filters are already there and are
   * not cleared on navigation, so the list opens scoped to both.
   */
  const openDeviceList = useCallback((attribute: string) => () => {
    dispatch(setSelectedAttribute(attribute));
    dispatch(pushHistory(DEVICE_LIST_PATH));
  }, [dispatch]);

  return (
    <DsRoot>
      <Helmet>
        <title>{title} - {t('dashboard.title')}</title>
      </Helmet>

      <FacilityMap>
        <DeviceSummaryBar
          loto={summary.get('loto')}
          offline={summary.get('offline')}
          alarming={summary.get('alarming')}
          totalDevices={summary.get('totalDevices')}
          forcedOn={summary.get('forcedOn')}
          onLotoClick={openDeviceList('loto')}
          onOfflineClick={openDeviceList('offline')}
          onAlarmingClick={openDeviceList('alarming')}
          onForcedOnClick={openDeviceList('forcedOn')}
          loading={showSkeletons}
        />
      </FacilityMap>
    </DsRoot>
  );
}
